import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import { LanguageProvider } from './context/LanguageContext';
import { AvailabilityProvider } from './context/AvailabilityContext';
import { HotelProvider } from './context/HotelContext';
import Header from './components/Header';
import Hero from './components/Hero';
import WhyBookDirect from './components/WhyBookDirect';
import Rooms from './components/Rooms';
import Reviews from './components/Reviews';
import Footer from './components/Footer';
import ExploreGrid from './components/ExploreGrid';
import SectionPage from './pages/SectionPage';
import SeasonPage from './pages/SeasonPage';
import TopicPage from './pages/TopicPage';
import { TOPIC_SLUGS } from './i18n/topics';
import { SEASON_SLUGS } from './i18n/seasons';
import { SECTIONS, SUPPORTED_LANGS as SITE_LANGS } from './siteRoutes';
import RoomDetail from './components/RoomDetail';
import SEOHead from './components/SEOHead';
import BookingPage from './pages/BookingPage';
import BookingConfirmation from './pages/BookingConfirmation';
import OfferPage from './pages/OfferPage';
import PartnerPortal from './pages/PartnerPortal';
import AdminPanel from './pages/AdminPanel';
import CouponRedeem from './pages/CouponRedeem';
import InRoom from './pages/InRoom';
import TerracePage from './pages/Terrace';
import ShiftScan from './pages/ShiftScan';
import SelfCheckin from './pages/SelfCheckin';
import NotFound from './pages/NotFound';
import KeyFacts from './components/KeyFacts';
import BeachNowStrip from './components/BeachNowStrip';
import BookingReviewsStrip from './components/BookingReviewsStrip';
import RoomFinderQuiz from './components/RoomFinderQuiz';
import WhatToExpect from './components/WhatToExpect';
import GuestPhotoWall from './components/GuestPhotoWall';
import PhotoUploadPage from './pages/PhotoUploadPage';
import TrustPage from './pages/TrustPage';
import GuestsAsked from './components/GuestsAsked';
import { buildHotelGraph } from './seo/hotelSchema';
import { useHotel } from './context/HotelContext';
import { getSEO } from './i18n/seo';
import ChatWidget from './components/ChatWidget';
import LanguageHint from './components/LanguageHint';
import DirectBookingCoupon from './components/DirectBookingCoupon';
import { useLanguage } from './context/LanguageContext';
import { trackPageView } from './services/api';

const SUPPORTED_LANGS = ['en', 'me', 'sq', 'de', 'ru', 'pl', 'hu'];

// Bots/crawlers we deliberately do NOT redirect — they should be able to crawl all
// language variants from the bare `/` URL via hreflang. Pattern based on user-agent.
const BOT_UA_PATTERN = /(bot|crawl|spider|GPTBot|ChatGPT|Claude|Perplexity|Google-Extended|OAI-SearchBot|Applebot|Bytespider|Amazonbot|Meta-ExternalAgent|cohere-ai|DuckAssistBot|Yandex|Baidu|Slurp|DuckDuck)/i;

const userPickedLangBefore = () => {
  try {
    return !!localStorage.getItem('hotel-language');
  } catch {
    return false;
  }
};

const browserPreferredLocale = () => {
  if (typeof navigator === 'undefined') return null;
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const l of langs) {
    if (!l) continue;
    const code = l.toLowerCase().split('-')[0];
    // Map sr → me (Montenegrin uses sr-ME) so Serbian-locale users land on /me/
    const mapped = code === 'sr' ? 'me' : code;
    if (SUPPORTED_LANGS.includes(mapped)) return mapped;
  }
  return null;
};

// Detects /:lang/ path prefix and syncs the LanguageContext.
function LanguageRouteSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setLang } = useLanguage();

  // 1) URL → context: /{lang}/ prefix wins; an explicit `state.lang` (header switch to English = no prefix) too.
  //    Depends on the path only — reacting to `lang` here raced the header switch and flipped it straight back.
  useEffect(() => {
    const seg = location.pathname.split('/').filter(Boolean)[0];
    const fromState = location.state && location.state.lang;
    if (seg && SUPPORTED_LANGS.includes(seg)) setLang(seg);
    else if (fromState && SUPPORTED_LANGS.includes(fromState)) setLang(fromState);
  }, [location.pathname, location.state, setLang]);

  // 2) On first visit to bare `/`, auto-redirect to /:browserLang/ when applicable.
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (location.search) return; // honour explicit ?lang=
    if (userPickedLangBefore()) return; // they have chosen — leave them on /
    if (typeof navigator !== 'undefined' && BOT_UA_PATTERN.test(navigator.userAgent || '')) return;
    const target = browserPreferredLocale();
    if (target && target !== 'en') {
      // Replace (not push) so the back button isn't broken.
      navigate(`/${target}/`, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
}

// Private / transactional pages must never be indexed (booking funnel, offers, self check-in, in-room, admin).
const PRIVATE_PREFIXES = ['/booking', '/offer/', '/partner/', '/admin', '/redeem/', '/inroom/', '/terrace/', '/checkin/', '/shift-scan'];
const isPrivatePath = (p) => PRIVATE_PREFIXES.some((x) => p.startsWith(x) || SUPPORTED_LANGS.some((l) => p.startsWith(`/${l}${x}`)));
function RobotsGuard() {
  const location = useLocation();
  useEffect(() => {
    const value = isPrivatePath(location.pathname) ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1';
    ['robots', 'googlebot'].forEach((name) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', value);
    });
  }, [location.pathname]);
  return null;
}

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();
  
  useEffect(() => {
    // Don't track admin pages
    if (!location.pathname.startsWith('/admin')) {
      trackPageView(location.pathname);
    }
  }, [location]);
  
  return null;
}

// Handles `/en/#gallery`-style URLs: after content renders, scroll to the target section.
// Necessary because the browser's built-in hash-scroll fires before React has rendered
// the section, and because our LanguageRouteSync may replace() the URL and strip focus.
function HashScrollHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Legacy `/#gallery`-style links: those sections now live on their own page.
    const section = SECTIONS.find((s) => s.id === id && s.slug !== 'rooms');
    if (section && !document.getElementById(id)) {
      navigate(`${location.pathname.replace(/\/$/, '')}/${section.slug}`.replace(/^\/\//, '/'), { replace: true });
      return undefined;
    }
    // Poll for the element for up to 2s (covers async data-driven sections).
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        clearInterval(timer);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (++tries > 20) {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [location.pathname, location.hash]);

  return null;
}

// Chat widget wrapper - only show on public pages
function ChatWidgetWrapper() {
  const location = useLocation();
  
  // Don't show chat on admin, booking, or in-room pages
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/booking') || location.pathname.startsWith('/inroom') || location.pathname.startsWith('/terrace') || location.pathname.startsWith('/partner/')) {
    return null;
  }
  
  return <ChatWidget />;
}

function HomePage() {
  const { lang } = useLanguage();
  const { rooms, hotelInfo } = useHotel();
  const schema = React.useMemo(() => buildHotelGraph({ lang, rooms, hotelInfo, description: getSEO(lang).description }), [lang, rooms, hotelInfo]);
  return (
    <div>
      <SEOHead url="/" type="website" schema={schema} faq />
      <Header />
      <Hero />
      <BeachNowStrip />
      <KeyFacts />
      <WhatToExpect />
      <WhyBookDirect />
      <BookingReviewsStrip />
      <Rooms limit={3} />
      <RoomFinderQuiz />
      <GuestPhotoWall />
      <ExploreGrid exclude={['rooms']} />
      <Reviews />
      <GuestsAsked />
      <Footer />
    </div>
  );
}

const SEASON_ROUTES = ['', ...SITE_LANGS.map((l) => `/${l}`)].flatMap((p) => SEASON_SLUGS.map((m) => <Route key={`${p}/ulcinj-in-${m}`} path={`${p}/ulcinj-in-${m}`} element={<SeasonPage month={m} />} />));

const TOPIC_ROUTES = ['', ...SITE_LANGS.map((l) => `/${l}`)].flatMap((p) => TOPIC_SLUGS.map((sl) => <Route key={`${p}/stay/${sl}`} path={`${p}/stay/${sl}`} element={<TopicPage slug={sl} />} />));

const SECTION_ROUTES = SECTIONS.flatMap((s) => ['', ...SITE_LANGS.map((l) => `/${l}`)].map((p) => (
  <Route key={`${p}/${s.slug}`} path={`${p}/${s.slug}`} element={<SectionPage slug={s.slug} />} />
)));

const TRUST_ROUTES = ['', ...SITE_LANGS.map((l) => `/${l}`)].map((p) => <Route key={`${p}/trust`} path={`${p}/trust`} element={<TrustPage />} />);

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <HotelProvider>
          <AvailabilityProvider>
            <Router>
              <AnalyticsTracker />
              <LanguageRouteSync />
              <RobotsGuard />
              <HashScrollHandler />
              <div className="App">
                <Routes>
                  {SEASON_ROUTES}
            {TOPIC_ROUTES}
                  {TRUST_ROUTES}
                  {/* Default (English / x-default) */}
                  <Route path="/" element={<HomePage />} />
                  {SECTION_ROUTES}
                  <Route path="/room/:id" element={<RoomDetail />} />
                  <Route path="/booking" element={<BookingPage />} />
                  <Route path="/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/en/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/me/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/sq/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/de/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/ru/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/pl/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/hu/photos/upload" element={<PhotoUploadPage />} />
                  <Route path="/booking-confirmed" element={<BookingConfirmation />} />
                  <Route path="/offer/:token" element={<OfferPage />} />
                  <Route path="/partner/:token" element={<PartnerPortal />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/redeem/:code" element={<CouponRedeem />} />
                  <Route path="/inroom/:roomId" element={<InRoom />} />
                  <Route path="/terrace/:spotId" element={<TerracePage />} />
                  <Route path="/shift-scan" element={<ShiftScan />} />
                  <Route path="/checkin/:token" element={<SelfCheckin />} />

                  {/* Per-locale path-prefixed variants — same components, language picked up via LanguageRouteSync */}
                  <Route path="/en" element={<HomePage />} />
                  <Route path="/me" element={<HomePage />} />
                  <Route path="/sq" element={<HomePage />} />
                  <Route path="/de" element={<HomePage />} />
                  <Route path="/ru" element={<HomePage />} />
                  <Route path="/pl" element={<HomePage />} />
                  <Route path="/hu" element={<HomePage />} />

                  <Route path="/en/room/:id" element={<RoomDetail />} />
                  <Route path="/me/room/:id" element={<RoomDetail />} />
                  <Route path="/sq/room/:id" element={<RoomDetail />} />
                  <Route path="/de/room/:id" element={<RoomDetail />} />
                  <Route path="/ru/room/:id" element={<RoomDetail />} />
                  <Route path="/pl/room/:id" element={<RoomDetail />} />
                  <Route path="/hu/room/:id" element={<RoomDetail />} />

                  <Route path="/en/booking" element={<BookingPage />} />
                  <Route path="/me/booking" element={<BookingPage />} />
                  <Route path="/sq/booking" element={<BookingPage />} />
                  <Route path="/de/booking" element={<BookingPage />} />
                  <Route path="/ru/booking" element={<BookingPage />} />
                  <Route path="/pl/booking" element={<BookingPage />} />
                  <Route path="/hu/booking" element={<BookingPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <ChatWidgetWrapper />
                <LanguageHint />
                <DirectBookingCoupon />
              </div>
            </Router>
          </AvailabilityProvider>
        </HotelProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
